import { FormEvent, useState } from 'react'
import { useLoaderData, useNavigate, useParams } from 'react-router-dom'
import type { LoaderFunctionArgs } from 'react-router-dom'
import { useCurrentHealthCenter } from '../../components/AppShell'
import { Button, DateField, Panel, PanelBody, PanelHeading, TextAreaField, TextField, useToast } from '../../components/common'
import { TreatmentTemplate, createProgramOfTreatment, fetchTreatmentTemplates } from '../../api/treatments'
import { ApiError } from '../../lib/api'
import { trackEvent } from '../../lib/config'
import { formatMoney } from '../../lib/currency'

function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.body !== null && typeof error.body === 'object') {
    const messages = Object.entries(error.body as Record<string, string[]>).map(
      ([field, fieldMessages]) => `${field} ${fieldMessages.join(', ')}`,
    )
    if (messages.length > 0) {
      return messages.join(' · ')
    }
  }
  return 'No pudimos completar la operación. Intenta nuevamente.'
}

type Line = { code: string; price: string; note: string }

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function byCode(templates: readonly TreatmentTemplate[]): readonly TreatmentTemplate[] {
  return [...templates].sort((left, right) => (left.code ?? '').localeCompare(right.code ?? ''))
}

function LineFields({
  templates,
  line,
  onChange,
}: {
  templates: readonly TreatmentTemplate[]
  line: Line
  onChange: (line: Line) => void
}) {
  const selectTemplate = (code: string) => {
    const template = templates.find((candidate) => candidate.code === code)
    onChange({ ...line, code, price: template?.price ?? line.price })
  }

  return (
    <>
      <div className="col-xs-4">
        <select className="form-control" value={line.code} onChange={(event) => selectTemplate(event.target.value)}>
          {templates.map((template) => (
            <option key={template.id} value={template.code ?? ''}>
              {`${template.code} : ${template.name}`}
            </option>
          ))}
        </select>
      </div>
      <div className="col-xs-4">
        <input type="text" value={line.price} onChange={(event) => onChange({ ...line, price: formatMoney(event.target.value) })} />
      </div>
      <div className="col-xs-4">
        <input type="text" value={line.note} onChange={(event) => onChange({ ...line, note: event.target.value })} />
      </div>
    </>
  )
}

export async function loader({ params }: LoaderFunctionArgs): Promise<readonly TreatmentTemplate[]> {
  return byCode(await fetchTreatmentTemplates(Number(params.healthCenterId)))
}

export default function ProgramOfTreatmentFormPage() {
  const healthCenter = useCurrentHealthCenter()
  const navigate = useNavigate()
  const showToast = useToast()
  const params = useParams()
  const patientId = params.patientId ?? ''
  const templates = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [date, setDate] = useState(today())
  const [lines, setLines] = useState<readonly Line[]>([{ code: templates[0]?.code ?? '', price: '', note: '' }])

  if (!healthCenter) {
    return null
  }

  const setLine = (index: number, line: Line) =>
    setLines((current) => current.map((existing, position) => (position === index ? line : existing)))

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    try {
      await createProgramOfTreatment(healthCenter.id, patientId, {
        date,
        name,
        description,
        treatments: lines.map((line) => line.code),
        treatments_price: lines.map((line) => line.price),
        treatments_note: lines.map((line) => line.note),
      })
    } catch (error) {
      const isValidation = error instanceof ApiError && error.status === 422
      trackEvent('Form Save Failed', {
        form: 'program',
        reason: isValidation ? 'validation' : 'request',
        status_code: error instanceof ApiError ? error.status : undefined,
        fields: isValidation && error.body && typeof error.body === 'object' ? Object.keys(error.body) : undefined,
      })
      showToast('error', getErrorMessage(error))
      return
    }
    trackEvent('Program Of Treatment Created', { line_count: lines.length })
    navigate(`/health_centers/${healthCenter.id}/patients/${patientId}/treatments`)
  }

  return (
    <Panel>
      <PanelHeading>
        <h1>Nuevo Programa de Tratamientos</h1>
      </PanelHeading>
      <PanelBody className="container">
        <form onSubmit={(event) => void handleSubmit(event)}>
          <div className="row">
            <TextField className="col-xs-12" label="Profesional" labelStyle="plain" value={name} onChange={setName} />
            <TextAreaField
              className="col-xs-12"
              label="Descripción"
              labelStyle="plain"
              rows={8}
              value={description}
              onChange={setDescription}
            />
            <div className="col-xs-12">
              <DateField label="Fecha" labelStyle="plain" value={date} onChange={setDate} />
            </div>
            <br />
            <div className="col-xs-12">
              <div className="field">
                <div style={{ textAlign: 'center' }} className="col-xs-10">
                  <div className="col-xs-4">Tratamiento</div>
                  <div className="col-xs-4">Precio</div>
                  <div className="col-xs-4">Nota</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  {lines.map((line, index) => (
                    <div key={index}>
                      <div className="col-xs-10" style={{ margin: 0 }}>
                        <LineFields templates={templates} line={line} onChange={(next) => setLine(index, next)} />
                      </div>
                      <div className="col-xs-2">
                        {index > 0 && (
                          <Button
                            variant="danger"
                            style={{ display: 'inline-block' }}
                            onClick={() => setLines((current) => current.filter((_, position) => position !== index))}
                          >
                            -
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <Button
                    variant="success"
                    onClick={() => setLines((current) => [...current, { code: templates[0]?.code ?? '', price: '', note: '' }])}
                  >
                    +
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <br />
          <div className="actions">
            <Button type="submit">Crear</Button>
          </div>
        </form>
      </PanelBody>
    </Panel>
  )
}
